"use client";

import { FormEvent, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Search, Wrench } from "lucide-react";
import { RentalDatePicker } from "@/components/ui/rental-date-picker";

interface SearchProduct {
  id: number;
  title: string;
  slug: string;
  selling_type: string | null;
}

type SearchMode = "achat" | "location";

function today(): string {
  return new Date().toISOString().split("T")[0];
}

export function MobileProductFinderSection() {
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<SearchMode>("location");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<SearchProduct | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2 || selectedProduct?.title === query) {
      setResults([]);
      setOpen(false);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `/api/search?q=${encodeURIComponent(query.trim())}&limit=6&type=${mode}`,
          { signal: controller.signal }
        );
        if (!response.ok) return;
        const data = await response.json();
        setResults(data.products || []);
        setOpen(true);
      } catch (error) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setResults([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [mode, query, selectedProduct]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [open]);

  const changeMode = (nextMode: SearchMode) => {
    setMode(nextMode);
    setQuery("");
    setSelectedProduct(null);
    setResults([]);
    setOpen(false);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    params.set("type", mode);
    if (mode === "location") {
      if (startDate) params.set("rentalStartDate", startDate);
      if (endDate) params.set("rentalEndDate", endDate);
    }

    if (selectedProduct && selectedProduct.title === query) {
      const dateParams = new URLSearchParams();
      if (mode === "location") {
        if (startDate) dateParams.set("rentalStartDate", startDate);
        if (endDate) dateParams.set("rentalEndDate", endDate);
      }
      const suffix = dateParams.toString() ? `?${dateParams.toString()}` : "";
      router.push(`/produits/${selectedProduct.slug}${suffix}`);
      return;
    }

    if (query.trim()) {
      router.push(`/recherche?${params.toString()}`);
    } else {
      router.push(`/categories?type=${mode}`);
    }
  };

  return (
    <section className="bg-gray-50 md:hidden">
      <div className="container mx-auto px-4 pt-7 pb-4">
        <h2 className="whitespace-nowrap text-center text-[clamp(1.05rem,5vw,1.25rem)] font-bold leading-tight tracking-tight">
          Que souhaitez-vous rechercher ?
        </h2>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-primary px-5 py-7"
        style={{
          backgroundImage: "url('/textures/mobile-product-finder.png')",
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      >
        <div className="mx-auto max-w-md">
          <div className="mb-5 flex items-center justify-center gap-3">
            <span className="text-xl font-bold text-secondary">Je souhaite</span>
            <div className="flex overflow-hidden rounded-md bg-white shadow-sm">
              <button
                type="button"
                onClick={() => changeMode("achat")}
                className={`min-w-24 px-4 py-1.5 text-sm font-medium transition-colors ${
                  mode === "achat" ? "bg-secondary text-white" : "text-gray-600"
                }`}
              >
                Acheter
              </button>
              <button
                type="button"
                onClick={() => changeMode("location")}
                className={`min-w-24 px-4 py-1.5 text-sm font-medium transition-colors ${
                  mode === "location" ? "bg-secondary text-white" : "text-gray-600"
                }`}
              >
                Louer
              </button>
            </div>
          </div>

          <div className="relative" ref={containerRef}>
            <Wrench className="pointer-events-none absolute left-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-primary" />
            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setSelectedProduct(null);
              }}
              onFocus={() => results.length > 0 && setOpen(true)}
              placeholder={mode === "location" ? "Rechercher un produit à la location..." : "Rechercher un produit à l'achat..."}
              className="h-14 w-full rounded-lg border-0 bg-white pl-12 pr-4 text-sm text-gray-800 shadow-sm outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-secondary/30"
            />

            {open && (
              <div className="absolute left-0 right-0 top-full z-40 mt-1 overflow-hidden rounded-xl border bg-white shadow-xl">
                {loading ? (
                  <p className="px-4 py-3 text-sm text-gray-400">Recherche en cours...</p>
                ) : results.length > 0 ? (
                  results.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => {
                        setSelectedProduct(product);
                        setQuery(product.title);
                        setOpen(false);
                      }}
                      className="block w-full border-b px-4 py-3 text-left text-sm text-gray-700 last:border-b-0 hover:bg-secondary/5 hover:text-secondary"
                    >
                      {product.title}
                    </button>
                  ))
                ) : (
                  <p className="px-4 py-3 text-sm text-gray-400">Aucun produit trouvé</p>
                )}
              </div>
            )}
          </div>

          {mode === "location" && (
            <div className="mt-4 grid grid-cols-2 gap-1">
              <RentalDatePicker
                value={startDate}
                onChange={(value) => {
                  setStartDate(value);
                  if (endDate && endDate < value) setEndDate("");
                }}
                min={today()}
                placeholder="Date de début"
                triggerClassName="h-14 rounded-r-none border-0"
              />
              <RentalDatePicker
                value={endDate}
                onChange={setEndDate}
                min={startDate || today()}
                placeholder="Date de fin"
                dropdownAlign="right"
                triggerClassName="h-14 rounded-l-none border-0"
              />
            </div>
          )}

          <div className="mt-4 flex justify-center">
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-lg bg-secondary px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-secondary/90"
            >
              <Search className="h-5 w-5" />
              Rechercher
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}
